import cx from 'classnames';
import truncate from 'lodash-es/truncate';
import {
    ANONYMOUS_USERNAME,
    FORUM_REPORTED,
    NEW_FEMININE,
    POST_DELETED_BY_ADMIN,
    POST_DELETED_BY_AUTHOR,
    SHOW,
} from 'mk/autogenerated/translations/FeedPost.137c33e2bb96a5fd5817bb6634a7f979'
import { upperCaseFirst } from 'mk/bazaar/common/filters';
import { isAnonymousUser } from 'mk/bazaar/common/userUtils';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import { postDetailUrl, topicUrl } from 'mk2/apps/forum/helpers';
import { PostEntity } from 'mk2/apps/forum/schemas';
import Emojify from 'mk2/components/Emojify';
import Link from 'mk2/components/Link';
import { dateDiff, timeAgo, timeAt } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import React from 'react';
import styles from './FeedPost.mscss';

interface OwnProps {
    post: PostEntity;
    mobile: boolean;
    className?: string;
    readonlyPosts: boolean;
    showTopic?: boolean;
    showSticky?: boolean;
    showCreated?: boolean;
    showAuthor?: boolean;
    showCheckMark?: boolean;
    showNewBadge?: boolean;
    alignDateLeft?: boolean;
    complaintsCount?: number;

    onTopicClickedStoreEvent?();
}

const lessThanDay = (d: string | Date) => {
    const { diffDays, diffSeconds } = dateDiff(d, new Date());
    return diffDays < 1;
};

const NewOrNumber = (isNew: boolean, numComments: number) => {
    return isNew
        ? (
            <span className={styles.FeedPost__badgeNew}>
                {NEW_FEMININE}
            </span>
        ) : numComments;
};

export default class FeedPost extends React.Component<OwnProps> {

    /**
     *  height of post (including border and padding)
     */
    public static height(post: PostEntity, isMobile: boolean): number {
        return (
            post.deletedAt ? 34 : (isMobile ? 92 : 68)
        );
    }

    /**
     *  height of post (including margins)
     */
    public static outerHeight(post: PostEntity, isMobile: boolean): number {
        return (
            FeedPost.height(post, isMobile)
        );
    }

    /**
     *  maximal value that outerHeight() might return
     */
    public static outerHeightMax(isMobile: boolean): number {
        return isMobile ? 92 : 68;
    }

    public shouldComponentUpdate(n: OwnProps) {
        const p = this.props;

        return p.post.id !== n.post.id || p.post.comments !== n.post.comments
            || p.post.sticky !== n.post.sticky
            || p.post.likeable.likesCount !== n.post.likeable.likesCount
            || p.post.likeable.isLiked !== n.post.likeable.isLiked
            || p.post.deletedAt !== n.post.deletedAt
            || p.complaintsCount !== n.complaintsCount;
    }

    public render() {
        const {
            post, complaintsCount,
            className, mobile,
            showTopic, showSticky, showCreated, showAuthor, showCheckMark, alignDateLeft,
            onTopicClickedStoreEvent,
            readonlyPosts, showNewBadge,
        } = this.props;

        const isNew = showNewBadge && post.created && lessThanDay(post.created);

        return post.deletedAt ? (
            <div
                className={cx(
                    styles.FeedPost,
                    styles['FeedPost--deleted'],
                    className,
                )}
                style={{height: FeedPost.height(post, mobile)}}
            >
                <a
                    href={url(blogsProfileUrl, { username: post.author.username })}
                    className={styles.FeedPost__deletedAuthor}
                >
                    {post.author.username}
                </a>

                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                {post.deletedByAuthor ? POST_DELETED_BY_AUTHOR : POST_DELETED_BY_ADMIN}
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;

                <Link to={postDetailUrl(post)}>
                    {SHOW}
                </Link>
            </div>
        ) : (
            <div
                className={cx(
                    styles.FeedPost,
                    styles['FeedPost--active'],
                    className,
                    complaintsCount && styles['FeedPost--has-complaints'],
                    readonlyPosts && styles['FeedPost--readonlyPost'],
                )}
                style={{height: FeedPost.height(post, mobile)}}
            >
                <div className={styles.FeedPost__header}>
                    <Link
                        className={cx(styles.FeedPost__header__heading, post.contestEnded && styles['FeedPost__header__heading--ended'])}
                        to={postDetailUrl(post)}
                    >
                        <h2 className={styles.FeedPost__header__heading__text}>
                            {showSticky && post.sticky && (
                                <i className={cx(styles.FeedPost__header__stickyFa, 'fa fa-thumb-tack fa-lg')} />
                            )}

                            <Emojify>
                                {upperCaseFirst(truncate(post.heading, { length: mobile ? 75  : 200 }))}
                            </Emojify>
                        </h2>
                    </Link>

                    {showCheckMark ? (
                        <span className={styles.FeedPost__header__checkmark}>
                            <i className="fa fa-check" />
                        </span>
                    ) : readonlyPosts ? (
                        <div
                            className={cx(
                                styles.FeedPost__header__comments,
                                isNew && styles['FeedPost__header__comments--new'],
                            )}
                        >
                            {NewOrNumber(isNew, post.comments)}
                        </div>
                    ) : (
                        <Link
                            className={cx(
                                styles.FeedPost__header__comments,
                                isNew && styles['FeedPost__header__comments--new'],
                            )}
                            to={postDetailUrl(post, { page: 'last', scrollto: 'last' })}
                            rel="nofollow"
                        >
                            {NewOrNumber(isNew, post.comments)}
                        </Link>
                    )}
                </div>

                <div className={styles.FeedPost__footer}>
                    <div className={cx(styles.FeedPost__footer__left, alignDateLeft && styles['FeedPost__footer__left--displayNone'])}>
                        {complaintsCount > 0 && (
                            <Link
                                className={styles.FeedPost__footer__left__reported}
                                to={postDetailUrl(post)}
                            >
                                {complaintsCount}x {FORUM_REPORTED}
                            </Link>
                        )}

                        {complaintsCount > 0 && (
                            <span className={styles.FeedPost__sep}>•</span>
                        )}

                        {showTopic ? (
                            <Link
                                className={styles.FeedPost__footer__left__link}
                                to={topicUrl(post.topic)}
                                onClick={onTopicClickedStoreEvent}
                            >
                                <span>{upperCaseFirst(post.topic.name)}</span>
                            </Link>
                        ) : showAuthor ? (
                            isAnonymousUser(post.author) ? (
                                <span className={styles.FeedPost__footer__left__link}>
                                    {ANONYMOUS_USERNAME}
                                </span>
                            ) : (
                                <a
                                    className={styles.FeedPost__footer__left__link}
                                    href={url(blogsProfileUrl, { username: post.author.username })}
                                >
                                    {post.author.username}
                                </a>
                            )
                        ) : null}
                    </div>

                    {readonlyPosts ? (
                        <div
                            className={styles.FeedPost__footer__date}
                            title={timeAt(showCreated ? post.created : post.lastMessageTime, null, true)}
                        >
                            {timeAgo(showCreated ? post.created : post.lastMessageTime, null, true)}
                        </div>
                    ) : (
                        <Link
                            className={styles.FeedPost__footer__date}
                            to={postDetailUrl(post, { page: 'last', scrollto: 'last' })}
                            title={timeAt(showCreated ? post.created : post.lastMessageTime, null, true)}
                            rel="nofollow"
                        >
                            {timeAgo(showCreated ? post.created : post.lastMessageTime, null, true)}
                        </Link>
                    )}
                </div>
            </div>
        );
    }
}
